import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, DatePicker, Input, MultiSelect, StringSelect, useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { parseDateInput } from "@tellescope/utils";

export type CustomFieldValue = boolean | null | string | number | object | undefined;

export interface CustomFieldEditorProps {
  id: string;
  type: string;
  options?: string[];
  value: CustomFieldValue;
  onChange: (next: CustomFieldValue) => void;
  onCancel?: () => void;
  userId?: string;
}

const toStringArray = (value: CustomFieldValue): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

// TODO: replace this minimal inline custom-field input with the abstracted design-system
//   custom-field-input component once one exists (this covers only the common editable types;
//   Table / Database Select / File render read-only elsewhere).
// Part of that should be majorly simplifying this state management in connection with the
// custom contact details sidebar UI
export const CustomFieldEditor = ({
  id,
  type,
  options: rawOptions,
  value,
  onChange,
  onCancel,
  userId,
}: CustomFieldEditorProps) => {
  const { t } = useTranslation("contacts");
  const { format } = useDateFnsFormattersWithLocale();
  const stringValue = typeof value === "string" ? value : "";
  const numString = typeof value === "number" ? String(value) : stringValue;

  const options = useMemo(() => (rawOptions ?? []).map((o) => ({ label: o, value: o })), [rawOptions]);

  // The text editors buffer keystrokes and report once, on blur or Enter: `onChange` saves to the
  // server, so reporting per keystroke fired a request per character and each response closed the
  // editor, taking focus with it. The pickers below stay on `onChange` — one selection is one edit.
  const [draft, setDraft] = useState(type === "Number" ? numString : stringValue);

  const commitText = () => {
    if (draft !== stringValue) onChange(draft);
  };

  // Kept as text while typing so partial input ("-", "1.") survives, and narrowed only on commit.
  const commitNumber = () => {
    if (draft === numString) return;
    if (draft === "") return onChange("");
    const parsed = Number(draft);
    onChange(Number.isNaN(parsed) ? draft : parsed);
  };

  switch (type) {
    case "Number":
      return (
        <Input
          id={id}
          type="number"
          value={draft}
          autoFocus
          onWheel={(e) => e.currentTarget.blur()}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitNumber();
            }
            if (e.key === "Escape") onCancel?.();
          }}
          onBlur={commitNumber}
        />
      );
    case "Date":
      return (
        <DatePicker
          id={id}
          value={parseDateInput(stringValue) ?? undefined}
          onChange={(d) => onChange(d ? format(d, "MM-dd-yyyy") : "")}
          className="space-y-0"
          label=""
        />
      );
    case "Select":
      return (
        <StringSelect
          value={stringValue}
          onValueChange={onChange}
          options={options}
          placeholder={t("Select an option")}
          className="w-full"
        />
      );
    case "Multiple Select":
      return (
        <MultiSelect
          id={id}
          value={toStringArray(value)}
          onValueChange={onChange}
          options={options}
          placeholder={t("Select options")}
        />
      );
    case "Multiple Text":
      return (
        <MultiSelect
          id={id}
          value={toStringArray(value)}
          onValueChange={onChange}
          options={toStringArray(value).map((v) => ({ label: v, value: v }))}
          creator={{
            render: (input) => t('Add "{{value}}"', { value: input }),
            transform: (input) => input,
            onCreate: () => {},
          }}
          placeholder={t("Add entries")}
        />
      );
    case "Checkbox": {
      const isChecked = stringValue.startsWith("checkbox:");
      return (
        <Checkbox
          id={id}
          checked={isChecked}
          onCheckedChange={(checked) =>
            onChange(checked === true ? `checkbox:${userId ?? ""}:${new Date().toISOString()}` : "")
          }
        />
      );
    }
    default:
      return (
        <Input
          id={id}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText();
            }
            if (e.key === "Escape") onCancel?.();
          }}
          onBlur={commitText}
        />
      );
  }
};
